<script lang="ts">
import { computed, defineComponent, h, unref } from 'vue';

import { classNames, convertToUnit } from '@components/utils';

import { makeThemeProps, provideTheme, useRender } from '../../composable';

import type { PropType } from 'vue';

type DividerKey = 'borderRightWidth' | 'borderTopWidth' | 'height' | 'width';
type DividerStyles = Partial<Record<DividerKey, string>>;

export const componentName = 'CdsDivider';
export default defineComponent({
  name: componentName,
  props: {
    /**
     * Устанавливает разделитель в вертикальное положение.
     */
    vertical: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Устанавливает длину разделителей. Единица измерения по умолчанию - px.
     */
    length: {
      type: [Number, String] as PropType<number | string>,
      default: undefined
    },
    /**
     * Устанавливает толщину разделителей. Единица измерения по умолчанию - px.
     */
    thickness: {
      type: [Number, String] as PropType<number | string>,
      default: undefined
    },
    ...makeThemeProps()
  },
  emits: {},
  setup(props, { attrs, slots }) {
    const { themeClasses } = provideTheme(props);

    const dividerStyles = computed(() => {
      const styles: DividerStyles = {};

      if (props.length) {
        styles[props.vertical ? 'height' : 'width'] = convertToUnit(props.length);
      }

      if (props.thickness) {
        styles[props.vertical ? 'borderRightWidth' : 'borderTopWidth'] = convertToUnit(props.thickness);
      }

      return styles;
    });

    useRender(() => {
      const divider = h('hr', {
        class: classNames(
          'cds-divider',
          {
            'cds-divider--vertical': props.vertical
          },
          unref(themeClasses)
        ),
        style: [unref(dividerStyles)],
        'aria-orientation':
          !attrs.role || attrs.role === 'separator' ? (props.vertical ? 'vertical' : 'horizontal') : undefined,
        role: `${attrs.role || 'separator'}`
      });

      if (!slots.default) return divider;

      return h(
        'div',
        {
          class: classNames('cds-divider__wrapper', {
            'cds-divider__wrapper--vertical': props.vertical
          })
        },
        [divider, h('div', { class: 'cds-divider__content' }, slots.default?.()), divider]
      );
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsDivider';
</style>
